import React, { useState, useMemo, useEffect } from 'react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { Invoice, InvoiceGroupDetails, InvoiceLineItem } from '../../../redux/billing/billing.types';
import { useUpdateInvoiceMutation, useUpdateInvoiceLineItemMutation, useCreateInvoiceLineItemMutation, useUpdateInvoiceStatusMutation, useSyncInvoiceMutation, useCreateInvoiceMutation, useResetInvoiceLineItemMutation, useResetInvoiceMutation } from '../../../redux/billing/billing.endpoints';
import { useGetPlatformsQuery } from "../../../redux/admin/reporting/reporting.endpoints";
import { useGetTermsQuery, useGetSKUsQuery } from "../../../redux/billing/billing.endpoints";

import {
  InvoiceRow,
  LineItemRow,
  InvoiceDetailsCard,
  LineItemDetailsCard,
  getCompanyName,
  getInvoiceChannelDisplay,
  getInvoiceTotals,
  formatNumber,
  formatCurrency,
  getTermNameById,
  getSkuNameById,
  formatPercentage,
  calculateInterconnectedValues,
} from './invoice-table/';
import { CircleUser, Eye, EyeOff, Plus, RefreshCw, Upload } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import ValidateUploadContainer from './invoice-table/validate-upload-container';
import InvoiceCreationContainer from './invoice-table/invoice-creation';
import { Company } from 'src/pages/billing/types/billing.types';
import { useGetCampaignsQuery, useGetInternalUsersQuery } from 'src/pages/client-management/endpoints/accounts.endpoints';
import { useSelector } from 'react-redux';
import { ReduxState } from 'src/redux/redux-state';

interface InvoiceTableProps {
  searchValue: string;
  // viewHidden: boolean;
  selectedGroupId: string;
  invoiceGroupDetails?: InvoiceGroupDetails;
  isLoadingDetails: boolean;
  isFetchingDetails: boolean;
  companies?: Company[];
  isGeneratingInvoices?: boolean;
  onGenerationComplete?: () => void;
  onOpenPreviewModal?: (groupId: string) => void;
  onGenerateInvoices?: (groupId: string) => void;
  refetchInvoiceGroups?: () => void;
}

export const InvoiceTableRefactored = ({
  searchValue,
  // viewHidden,
  selectedGroupId,
  invoiceGroupDetails,
  isLoadingDetails,
  isFetchingDetails,
  companies = [],
  isGeneratingInvoices = false,
  onGenerationComplete,
  onOpenPreviewModal,
  onGenerateInvoices,
  refetchInvoiceGroups,
}: InvoiceTableProps) => {
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(new Set());
  const [expandedLineItems, setExpandedLineItems] = useState<Set<string>>(new Set());
  const [newLineItemInputs, setNewLineItemInputs] = useState<Record<string, string>>({});
  const [showNewLineItemInputs, setShowNewLineItemInputs] = useState<Set<string>>(new Set());
  const [viewHidden, setViewHidden] = useState(false);
  const [showValidateUploadContainer, setShowValidateUploadContainer] = useState(false);
  const [showInvoiceCreationContainer, setShowInvoiceCreationContainer] = useState(false);
  const [filteredAllInvoices, setFilteredAllInvoices] = useState<Invoice[]>([]);

  // Local & optimistic states for patches & generation
  const [textFieldValues, setTextFieldValues] = useState<Record<string, string>>({});
  const [optimisticInvoiceGroupDetails, setOptimisticInvoiceGroupDetails] = useState<InvoiceGroupDetails | undefined>(invoiceGroupDetails);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filterUsersInvoices, setFilterUsersInvoices] = useState(false);

  const [useSyncInvoice] = useSyncInvoiceMutation();
  const [updateInvoiceStatus] = useUpdateInvoiceStatusMutation();
  const [createInvoice, { isLoading: isCreatingInvoice }] = useCreateInvoiceMutation();
  const [updateInvoice] = useUpdateInvoiceMutation();
  const [updateInvoiceLineItem, { isLoading: isUpdatingInvoiceLineItem }] = useUpdateInvoiceLineItemMutation();
  const [createInvoiceLineItem] = useCreateInvoiceLineItemMutation();
  const [resetInvoice, { isLoading: isResettingInvoice }] = useResetInvoiceMutation();
  const [resetInvoiceLineItem, { isLoading: isResettingInvoiceLineItem }] = useResetInvoiceLineItemMutation();

  const userState = useSelector((state: ReduxState) => state.userState);
  const {data: internalUsers = []} = useGetInternalUsersQuery();
  const { data: platforms = [] } = useGetPlatformsQuery({});
  const { data: terms = [] } = useGetTermsQuery();
  const { data: skus = [] } = useGetSKUsQuery();
  const { data: campaigns = [] } = useGetCampaignsQuery({});

  useEffect(() => {
    setIsLoading(isFetchingDetails || isLoadingDetails || isUpdatingInvoiceLineItem || isResettingInvoiceLineItem || isCreatingInvoice || isResettingInvoice);
  }, [isFetchingDetails, isLoadingDetails, isUpdatingInvoiceLineItem, isResettingInvoiceLineItem, isCreatingInvoice, isResettingInvoice]);

  useEffect(() => {
    if (invoiceGroupDetails) {
      setOptimisticInvoiceGroupDetails(invoiceGroupDetails);
    }
  }, [invoiceGroupDetails]);

  const handleCreateNewInvoice = async(companyId: string) => {
    try {
      await createInvoice({
        company: companyId,
        invoice_group: selectedGroupId,
        attention_needed: true,
      }).unwrap();

      // if (optimisticInvoiceGroupDetails) {
      //   setOptimisticInvoiceGroupDetails(prev => ({
      //     ...prev,
      //     invoices: [...prev.invoices, newInvoice]
      //   }));
      // }

      if (refetchInvoiceGroups) {
        await refetchInvoiceGroups();
      }
      setShowInvoiceCreationContainer(false);
    }
    catch (error) {
      console.error('Failed to create invoice:', error);
    }
  };


  // Helper functions and handlers
  // const handleCreateNewLineItem = async(invoiceId: string, name: string) => {
  //   try {
  //     await createInvoiceLineItem({
  //       name,
  //       invoice_group: selectedGroupId,
  //       invoice: invoiceId,
  //       attention_needed: true,
  //     }).unwrap();

  //     setNewLineItemInputs(prev => {
  //       const newInputs = { ...prev };
  //       delete newInputs[invoiceId];
  //       return newInputs;
  //     });
  //     setShowNewLineItemInputs(prev => {
  //       const newSet = new Set(prev);
  //       newSet.delete(invoiceId);
  //       return newSet;
  //     });

  //     if (refetchInvoiceGroupDetails) {
  //       await refetchInvoiceGroupDetails();
  //     }
  //   } catch (error) {
  //     console.error('Failed to create line item:', error);
  //   }
  // };

  // const handleNewLineItemInputChange = (invoiceId: string, value: string) => {
  //   setNewLineItemInputs(prev => ({
  //     ...prev,
  //     [invoiceId]: value,
  //   }));
  // };

  // const handleNewLineItemKeyDown = (invoiceId: string, e: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (e.key === 'Enter') {
  //     const name = newLineItemInputs[invoiceId]?.trim();
  //     if (name) {
  //       handleCreateNewLineItem(invoiceId, name);
  //     }
  //   }
  // };

  // const toggleNewLineItemInput = (invoiceId: string) => {
  //   setShowNewLineItemInputs(prev => {
  //     const newSet = new Set(prev);
  //     if (newSet.has(invoiceId)) {
  //       newSet.delete(invoiceId);
  //       setNewLineItemInputs(prevInputs => {
  //         const newInputs = { ...prevInputs };
  //         delete newInputs[invoiceId];
  //         return newInputs;
  //       });
  //     } else {
  //       newSet.add(invoiceId);
  //     }
  //     return newSet;
  //   });
  // };

  // Lookup objects (keeping existing logic)
  const companyLookup = useMemo(() => {
    if (!companies) return {};
    return companies.reduce((acc, company) => {
      acc[company.id] = company;
      return acc;
    }, {} as Record<string, Company>);
  }, [companies]);

  const termsLookup = useMemo(() => {
    if (!terms) return {};
    return terms.reduce((acc, term) => {
      acc[term.id] = term;
      return acc;
    }, {} as Record<string, any>);
  }, [terms]);

  const skusLookup = useMemo(() => {
    if (!skus) return {};
    return skus.reduce((acc, sku) => {
      acc[sku.id] = sku;
      return acc;
    }, {} as Record<string, any>);
  }, [skus]);

  // Sorted data (keeping existing logic)
  const sortedCompanyNames = useMemo(() => {
    if (!companies) return [];
    return companies
      .map(company => ({
        id: company.id,
        name: company.company_name || company.id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [companies]);

  const sortedTerms = useMemo(() => {
    if (!terms) return [];
    return terms
      .map(term => ({
        id: term.id,
        name: term.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [terms]);

  const sortedSKUs = useMemo(() => {
    if (!skus) return [];
    return skus
      .map(sku => ({
        id: sku.id,
        name: sku.name,
        sku: sku.sku || '',
        bill_type: sku.bill_type && sku.bill_type.toLowerCase().replace('_', ' '),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [skus]);

  // Sorted and filtered invoices - unassigned first, hide if no line items
  const sortedInvoices = useMemo(() => {
    const invoices = optimisticInvoiceGroupDetails?.invoices || [];
    return invoices
      .slice()
      // Filter out unassigned invoices with no line items
      .filter(invoice => {
        const isUnassigned = !invoice.company || !companyLookup[invoice.company];
        if (isUnassigned && invoice.invoice_line_items.length === 0) {
          return false; // Hide unassigned invoices with no line items
        }
        return true;
      })
      .sort((a, b) => {
        const aIsUnassigned = !a.company || !companyLookup[a.company];
        const bIsUnassigned = !b.company || !companyLookup[b.company];

        // Unassigned invoices always come first
        if (aIsUnassigned && !bIsUnassigned) return -1;
        if (!aIsUnassigned && bIsUnassigned) return 1;

        // If both are unassigned or both are assigned, sort by company name
        const aCompanyName = getCompanyName(a.company, companyLookup);
        const bCompanyName = getCompanyName(b.company, companyLookup);
        return aCompanyName.localeCompare(bCompanyName);
      })
      .map(invoice => ({
        ...invoice,
        invoice_line_items: invoice.invoice_line_items.slice().sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [optimisticInvoiceGroupDetails, companyLookup]);

  const filteredInvoices = useMemo(() => {
    if (!searchValue.trim()) {
      return sortedInvoices;
    }

    const searchLower = searchValue.toLowerCase();
    return sortedInvoices.filter(invoice => {
      const invoiceMatches =
        (invoice.id || '').toLowerCase().includes(searchLower) ||
        getCompanyName(invoice.company || '', companyLookup).toLowerCase().includes(searchLower) ||
        getInvoiceChannelDisplay(invoice).toLowerCase().includes(searchLower) ||
        formatNumber(getInvoiceTotals(invoice).impressions).includes(searchLower) ||
        formatCurrency(getInvoiceTotals(invoice).adSpend).toLowerCase().includes(searchLower) ||
        formatCurrency(getInvoiceTotals(invoice).amountToBill).toLowerCase().includes(searchLower) ||
        (invoice.invoice_status || '').toLowerCase().includes(searchLower) ||
        (companyLookup[invoice.company]?.emails || '').toLowerCase().includes(searchLower) ||
        (invoice.terms && getTermNameById(invoice.terms, termsLookup).toLowerCase().includes(searchLower)) ||
        (invoice.has_balance && 'has balance'.includes(searchLower)) ||
        (!invoice.has_balance && 'no balance'.includes(searchLower));

      const lineItemMatches = invoice.invoice_line_items.some(lineItem => {
        return (
          (lineItem.name || '').toLowerCase().includes(searchLower) ||
          (lineItem.id || '').toLowerCase().includes(searchLower) ||
          (lineItem.channel && lineItem.channel.toLowerCase().includes(searchLower)) ||
          formatNumber(lineItem.qty).includes(searchLower) ||
          formatCurrency(lineItem.advertiser_spend).toLowerCase().includes(searchLower) ||
          formatCurrency(lineItem.amount_to_bill || 0).toLowerCase().includes(searchLower) ||
          (lineItem.invoice_line_item_status || '').toLowerCase().includes(searchLower) ||
          (lineItem.platform && lineItem.platform.toLowerCase().includes(searchLower)) ||
          (lineItem.sku && getSkuNameById(lineItem.sku, skusLookup).toLowerCase().includes(searchLower)) ||
          (lineItem.net_spend !== null && formatCurrency(lineItem.net_spend).toLowerCase().includes(searchLower)) ||
          (lineItem.margin !== null && formatCurrency(lineItem.margin).toLowerCase().includes(searchLower)) ||
          (lineItem.margin_pct !== null && formatPercentage(lineItem.margin_pct).includes(searchLower)) ||
          (lineItem.notes && lineItem.notes.toLowerCase().includes(searchLower)) ||
          (lineItem.do_not_bill && 'do not bill'.includes(searchLower)) ||
          (!lineItem.do_not_bill && 'bill'.includes(searchLower))
        );
      });

      return invoiceMatches || lineItemMatches;
    });
  }, [sortedInvoices, searchValue, companyLookup, termsLookup, skusLookup]);

  // Text field management
  const getTextFieldValue = (fieldId: string, defaultValue: string): string => {
    return textFieldValues[fieldId] !== undefined ? textFieldValues[fieldId] : defaultValue;
  };

  const updateTextFieldValue = (fieldId: string, value: string) => {
    setTextFieldValues(prev => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const clearLineItemTextFields = (lineItemId: string) => {
    setTextFieldValues(prev => {
      const newValues = { ...prev };
      Object.keys(newValues).forEach(key => {
        if (key.endsWith(`_${lineItemId}`)) {
          delete newValues[key];
        }
      });
      return newValues;
    });
  };

  // Toggle functions
  const toggleInvoiceExpansion = (invoiceId: string) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceId)) {
      newExpanded.delete(invoiceId);
    } else {
      newExpanded.add(invoiceId);
    }
    setExpandedInvoices(newExpanded);
  };

  const toggleLineItemExpansion = (lineItemId: string) => {
    const newExpanded = new Set(expandedLineItems);
    if (newExpanded.has(lineItemId)) {
      newExpanded.delete(lineItemId);
    } else {
      newExpanded.add(lineItemId);
    }
    setExpandedLineItems(newExpanded);
  };

  // Update functions (keeping existing logic but simplified)
  const updateInvoiceField = async(invoiceId: string, field: string, value: any) => {
    try {
      const invoice = filteredInvoices.find(inv => inv.id === invoiceId);
      if (!invoice) {
        return;
      }

      if (field === 'reset') {
        await resetInvoice({ id: invoiceId }).unwrap();
        if (refetchInvoiceGroups) {
          await refetchInvoiceGroups();
        }
        return;
      }

      // Optimistically update the local state immediately
      if (optimisticInvoiceGroupDetails) {
        const updatedInvoices = optimisticInvoiceGroupDetails.invoices.map(inv =>
          inv.id === invoiceId ? { ...inv, [field]: value } : inv
        );
        setOptimisticInvoiceGroupDetails({
          ...optimisticInvoiceGroupDetails,
          invoices: updatedInvoices,
        });
      }


      await updateInvoice({
        id: invoiceId,
        data: {
          [field]: value,
          company: field === 'company' ? value : invoice.company,
          invoice_group: selectedGroupId,
        },
      }).unwrap();


      if (refetchInvoiceGroups) {
        refetchInvoiceGroups();
      }
    } catch (error) {
      console.error('Failed to update invoice:', error);
      setOptimisticInvoiceGroupDetails(invoiceGroupDetails);
    }
  };

  const updateLineItemField = async(lineItemId: string, field: string, value: any) => {
    try {
      if (field === 'reset') {
        await resetInvoiceLineItem({ id: lineItemId }).unwrap();
        if (refetchInvoiceGroups) {
          await refetchInvoiceGroups();
        }
        return;
      }

      let backendValue = value;
      if ((field === 'margin_pct' || field === 'margin_pct_override')) {
        // Convert percentage to decimal (e.g., 15 -> 0.15, 0 -> 0.00)
        backendValue = parseFloat(value) / 100;
      }

      const lineItem = filteredInvoices
        .flatMap(invoice => invoice.invoice_line_items)
        .find(item => item.id === lineItemId);

      if (!lineItem) {
        return;
      }

      const interconnectedUpdates = calculateInterconnectedValues(field, backendValue, lineItem, skus);
      const allUpdates = { [field]: backendValue, ...interconnectedUpdates };

      // Optimistically update the local state immediately
      if (field === 'invoice' && optimisticInvoiceGroupDetails) {
      // Remove line item from current invoice
        const updatedInvoices = optimisticInvoiceGroupDetails.invoices.map(invoice => ({
          ...invoice,
          invoice_line_items: invoice.invoice_line_items.filter(item => item.id !== lineItemId),
        }));

        // Add line item to target invoice
        const targetInvoiceIndex = updatedInvoices.findIndex(inv => inv.id === value);
        if (targetInvoiceIndex !== -1) {
          const lineItemToMove = optimisticInvoiceGroupDetails.invoices
            .flatMap(inv => inv.invoice_line_items)
            .find(item => item.id === lineItemId);

          if (lineItemToMove) {
            updatedInvoices[targetInvoiceIndex] = {
              ...updatedInvoices[targetInvoiceIndex],
              invoice_line_items: [
                ...updatedInvoices[targetInvoiceIndex].invoice_line_items,
                { ...lineItemToMove, ...allUpdates },
              ],
            };
          }
        }

        setOptimisticInvoiceGroupDetails({
          ...optimisticInvoiceGroupDetails,
          invoices: updatedInvoices,
        });
      } else if (optimisticInvoiceGroupDetails) {
      // Regular field updates (existing logic)
        const updatedInvoices = optimisticInvoiceGroupDetails.invoices.map(invoice => ({
          ...invoice,
          invoice_line_items: invoice.invoice_line_items.map(item =>
            item.id === lineItemId ? { ...item, ...allUpdates } : item
          ),
        }));
        setOptimisticInvoiceGroupDetails({
          ...optimisticInvoiceGroupDetails,
          invoices: updatedInvoices,
        });
      }

      const result = await updateInvoiceLineItem({
        id: lineItemId,
        data: {
          ...allUpdates,
          invoice_group: lineItem.invoice_group,
          invoice: field === 'invoice' ? value : lineItem.invoice,
        },
      }).unwrap();

      // Update optimistic state with the server response to ensure consistency
      if (optimisticInvoiceGroupDetails && result) {
        const updatedInvoices = optimisticInvoiceGroupDetails.invoices.map(invoice => ({
          ...invoice,
          invoice_line_items: invoice.invoice_line_items.map(item =>
            item.id === lineItemId ? result : item
          ),
        }));
        setOptimisticInvoiceGroupDetails({
          ...optimisticInvoiceGroupDetails,
          invoices: updatedInvoices,
        });
      }

      // Only refetch for certain operations that affect multiple items
      if (field === 'invoice' && refetchInvoiceGroups) {
        await refetchInvoiceGroups();
      }
    } catch (error) {
      console.error('Failed to update line item:', error);
      setOptimisticInvoiceGroupDetails(invoiceGroupDetails);
      clearLineItemTextFields(lineItemId);
    }
  };

  // Helper function
  const lineItemsNeedAttention = (invoice: Invoice) => {
    return invoice.invoice_line_items.some(item => item.attention_needed);
  };


  useEffect(() => {
    const activeUser = internalUsers.find(user => user.ext_user_id === userState.extUserId);
    if (filterUsersInvoices) {
      setFilteredAllInvoices(filteredInvoices.filter(invoice => invoice.invoice_line_items.some(lineItem => campaigns?.some(campaign => campaign.id === lineItem.campaign && campaign.owner === activeUser?.id))));
    } else {
      setFilteredAllInvoices(filteredInvoices);
    }
  }, [filterUsersInvoices, filteredInvoices, internalUsers, userState.extUserId, campaigns]);

  const handleFilterUsersInvoices = () => {
    setFilterUsersInvoices(!filterUsersInvoices);
  };

  const getOwner = (lineItem: InvoiceLineItem) => {
    const ownerEmail = internalUsers?.find(user => user.id === campaigns?.find(campaign => campaign.id === lineItem.campaign)?.owner)?.email;
    return ownerEmail || 'Unassigned';
  };

  // Early returns for different states
  // if (!selectedGroupId) {
  //   return <NoGroupSelectedState />;
  // }

  // if (isLoadingDetails) {
  //   return <LoadingState isGenerating={isGenerating} />;
  // }

  const isEmpty = filteredInvoices.length === 0;

  // if (isEmpty) {
  //   if (isGenerating && optimisticInvoiceGroupDetails) {
  //     return (
  //       <GeneratingInvoicesState
  //         month={optimisticInvoiceGroupDetails.month}
  //         year={optimisticInvoiceGroupDetails.year}
  //       />
  //     );
  //   }

  //   return (
  //     <NoInvoicesState
  //       month={optimisticInvoiceGroupDetails?.month}
  //       year={optimisticInvoiceGroupDetails?.year}
  //       onOpenPreviewModal={onOpenPreviewModal}
  //       onGenerateInvoices={onGenerateInvoices}
  //       selectedGroupId={selectedGroupId}
  //     />
  //   );
  // }

  // Main table render
  return (
    <div className="flex-grow min-h-0 flex flex-col">
      <div className={`w-full bg-white border border-gray-200 flex-grow min-h-0 flex flex-col rounded-xl relative ${isLoading ? 'opacity-50' : ''}`}>
        {isLoading && (
          <div className="sticky top-5 left-5 ml-5 flex justify-left items-center z-50 p-3 bg-white/60 rounded-full w-fit">
            <RefreshCw className="h-5 w-5 text-gray-600 animate-bounce animate-spin" />
          </div>
        )}
        <div className="flex-grow min-h-0 overflow-auto rounded-lg">
          {/* Menubar */}
          <div className='flex flex-row border-b border-gray-201'>
            <div className="flex flex-row gap-2 justify-start">
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {})}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 items-center ${filterUsersInvoices ? 'bg-gray-201' : ''}`}
              >
                <div className='text-gray-500 font-bold text-lg'>
                  {invoiceGroupDetails?.orphan_invoice_line_items.length}
                </div>
                Unassigned
              </Button>
            </div>
            <div className="flex flex-row gap-2 ml-auto">
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {
                  handleFilterUsersInvoices();
                })}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 ${filterUsersInvoices ? 'bg-gray-201' : ''}`}
              >
                <CircleUser className="h-4 w-4" />
                Filter
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {
                  setShowValidateUploadContainer(!showValidateUploadContainer);
                  setShowInvoiceCreationContainer(false);
                })}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 ${showValidateUploadContainer ? 'bg-gray-201' : ''}`}
              >
                <Upload className="h-4 w-4" />
                Validate
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => refetchInvoiceGroups?.())}
                className='flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1'
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {
                  setViewHidden(!viewHidden);
                })}
                className='flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1'
              >
                {viewHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {viewHidden ? 'Hide' : 'Show'}
              </Button>
              <Button
                size='med'
                variant='ghost'
                onClick={(() => {
                  setShowInvoiceCreationContainer(!showInvoiceCreationContainer);
                  setShowValidateUploadContainer(false);
                })}
                className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 ${showInvoiceCreationContainer ? 'bg-gray-201' : ''}`}
              >
                <Plus className="h-4 w-4" />
                Invoice
              </Button>
            </div>
          </div>
          {showValidateUploadContainer && (
            <ValidateUploadContainer
              invoiceGroupId={selectedGroupId}
              onUpload={() => {
                setShowValidateUploadContainer(false);
                setTimeout(() => refetchInvoiceGroups?.(), 1000);
              }}
            />
          )}
          {showInvoiceCreationContainer && (
            <InvoiceCreationContainer
              isCreating={isCreatingInvoice}
              companies={companies}
              onCreate={(companyId: string) => {
                handleCreateNewInvoice(companyId);
              }}
            />
          )}

          {/* Table */}
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10 rounded-t-lg">
              <TableRow className="border-b border-gray-201 rounded-t-lg">
                <TableHead className="w-[3%] min-w-[30px] py-1.5 rounded-tl-lg" />
                <TableHead className="text-gray-600 py-1.5 w-[35%] min-w-[250px] max-w-[250px]">
                  Invoice
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 w-[15%] min-w-[70px] max-w-[70px]">
                  Channel
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 text-right w-[15%] min-w-[100px] max-w-[100px]">
                  Quantity
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 text-right w-[15%] min-w-[100px] max-w-[100px]">
                  Amount to Bill
                </TableHead>
                <TableHead className="text-gray-600 py-1.5 text-center w-[15%] min-w-[100px] max-w-[100px]">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className='rounded-lg'>
              {filteredAllInvoices.filter(invoice => !viewHidden ? !invoice.hide : true).map((invoice, invoiceIndex) => {
                const isExpanded = expandedInvoices.has(invoice.id);
                const isUnassigned = !invoice.company || !companyLookup[invoice.company];
                const isFirstRow = invoiceIndex === 0;
                const isLastRow = invoiceIndex === filteredAllInvoices.length - 1;

                return (
                  <React.Fragment key={invoice.id}>
                    {/* Invoice Row */}
                    <InvoiceRow
                      invoice={invoice}
                      invoiceIndex={invoiceIndex}
                      isExpanded={isExpanded}
                      isFirstRow={isFirstRow}
                      isLastRow={isLastRow}
                      isUnassigned={isUnassigned}
                      viewHidden={viewHidden}
                      companyLookup={companyLookup}
                      selectedGroupId={selectedGroupId}
                      toggleInvoiceExpansion={toggleInvoiceExpansion}
                      updateInvoiceField={updateInvoiceField}
                      lineItemsNeedAttention={lineItemsNeedAttention}
                    />

                    {/* Invoice Details Card */}
                    {isExpanded && (
                      <InvoiceDetailsCard
                        invoice={invoice}
                        invoiceIndex={invoiceIndex}
                        companyLookup={companyLookup}
                        sortedCompanyNames={sortedCompanyNames}
                        sortedTerms={sortedTerms}
                        updateInvoiceField={updateInvoiceField}
                        refetchInvoiceGroupDetails={refetchInvoiceGroups || (() => {})}
                      />
                    )}

                    {/* Line Items */}
                    {isExpanded && invoice.invoice_line_items.filter(invoice_line_item => !viewHidden ? !invoice_line_item.hide : true).map((lineItem, lineItemIndex) => {
                      const isLineItemExpanded = expandedLineItems.has(lineItem.id);
                      const isLastRow = lineItemIndex === invoice.invoice_line_items.filter(invoice_line_item => !viewHidden ? !invoice_line_item.hide : true).length - 1;

                      return (
                        <React.Fragment key={lineItem.id}>
                          {/* Line Item Row */}
                          <LineItemRow
                            isLastRow={isLastRow}
                            lineItem={lineItem}
                            invoice={invoice}
                            invoiceIndex={invoiceIndex}
                            isLineItemExpanded={isLineItemExpanded}
                            viewHidden={viewHidden}
                            companies={companies}
                            filteredInvoices={filteredAllInvoices}
                            owner={getOwner(lineItem)}
                            toggleLineItemExpansion={toggleLineItemExpansion}
                            updateLineItemField={updateLineItemField}
                          />

                          {/* Line Item Details */}
                          {isLineItemExpanded && (
                            <LineItemDetailsCard
                              isLastRow={isLastRow}
                              lineItem={lineItem}
                              invoice={invoice}
                              companyLookup={companyLookup}
                              skus={skus}
                              invoices={invoiceGroupDetails?.invoices || []}
                              invoiceIndex={invoiceIndex}
                              platforms={platforms}
                              sortedSKUs={sortedSKUs}
                              textFieldValues={textFieldValues}
                              updateTextFieldValue={updateTextFieldValue}
                              updateLineItemField={updateLineItemField}
                              getTextFieldValue={getTextFieldValue}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}

                    {/* Add Line Item Row */}
                    {/* {isExpanded && (
                      <AddLineItemRow
                        invoice={invoice}
                        invoiceIndex={invoiceIndex}
                        isLastRow={isLastRow}
                        showNewLineItemInputs={showNewLineItemInputs}
                        newLineItemInputs={newLineItemInputs}
                        handleNewLineItemInputChange={handleNewLineItemInputChange}
                        handleNewLineItemKeyDown={handleNewLineItemKeyDown}
                        handleCreateNewLineItem={handleCreateNewLineItem}
                        toggleNewLineItemInput={toggleNewLineItemInput}
                      />
                    )} */}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default InvoiceTableRefactored;
